/**
 * AURA Engine
 * Main widget orchestrator - initializes and coordinates all modules
 */

import { AuraConfig, AuraSettings } from './types';
import { parseConfig } from './config';
import { stateManager } from './state';
import { eventBus } from './events';
import { cssInjector } from './css-injector';
import { domScanner } from './scanner';
import { Logger, configureLogger, configureRemoteLogging } from './logger';
import { destroyOrientationEffects } from '../modules/orientation/effects';
import { deactivateProfile as profilesDeactivate } from '../modules/profiles';

export interface AuraAPI {
  open: () => void;
  close: () => void;
  toggle: () => void;
  hide: () => void;
  show: () => void;
  getSettings: () => AuraSettings;
  setProfile: (profileName: string) => void;
  clearProfile: () => void;
  reset: () => void;
  on: (event: string, callback: (data?: unknown) => void) => () => void;
  off: (eventType: string) => void;
  destroy: () => void;
}

class AuraEngine {
  private initialized = false;
  private config: AuraConfig | null = null;
  private logger = new Logger('Engine');
  private shellElement: HTMLElement | null = null;

  /**
   * Initialize the widget
   */
  async init(): Promise<AuraAPI> {
    if (this.initialized) {
      this.logger.warn('Widget already initialized');
      return this.getApi();
    }

    this.logger.info('Initializing AURA widget');

    try {
      this.config = parseConfig();

      if (this.config.appId === 'development') {
        configureLogger({ level: 'debug' });
      }

      if (this.config.logApiUrl) {
        try {
          configureRemoteLogging({
            url: this.config.logApiUrl,
            appId: this.config.appId,
          });
        } catch (e) {
          this.logger.warn('Remote logging setup failed', { error: String(e) });
        }
      }

      try { cssInjector.init(); } catch (e) {
        this.logger.warn('CSS injector init failed', { error: String(e) });
      }

      await this.loadPersistedState();

      try { domScanner.start(); } catch (e) {
        this.logger.warn('DOM scanner failed to start', { error: String(e) });
      }

      await this.injectWidget();

      this.setupEventHandlers();

      this.initialized = true;
      eventBus.emit('ready');

      try { this.config.onReady?.(); } catch (e) {
        this.logger.warn('onReady callback threw', { error: String(e) });
      }

      this.logger.info('Widget initialized successfully');
      return this.getApi();
    } catch (error) {
      this.logger.error('Failed to initialize widget', error);
      try { this.config?.onError?.(error as Error); } catch (_) { /* swallow */ }
      throw error;
    }
  }

  /**
   * Load persisted state from localStorage or server
   */
  private async loadPersistedState(): Promise<void> {
    const storageKey = `aura-${this.config?.appId}`;

    try {
      const localData = localStorage.getItem(storageKey);
      if (localData) {
        stateManager.loadFromJson(localData);
        this.logger.debug('Loaded state from localStorage');
      }
    } catch (error) {
      this.logger.warn('localStorage unavailable, skipping persisted state', {
        error: String(error),
      });
    }

    if (this.config?.preferenceSyncApiUrl && this.config?.authTokenProvider) {
      try {
        const token = await this.config.authTokenProvider();
        if (token) {
          stateManager.setAuthenticated(true);
          this.logger.debug('User authenticated, server sync available');
        }
      } catch (error) {
        this.logger.warn('Auth token provider failed', { error });
      }
    }
  }

  /**
   * Inject the widget UI into the page
   */
  private async injectWidget(): Promise<void> {
    const { createWidgetShell } = await import('../ui/shell');
    this.shellElement = createWidgetShell(this.config!);

    // Append to <body> (not <html>).  Appending directly to <html> can
    // break host-page scripts that enumerate documentElement.children
    // and assume only <head>/<body> exist.  The widget's Shadow DOM
    // already isolates its internal styles, and the shell element itself
    // uses data-aura so the widget's own CSS overrides exclude it.
    const target = document.body ?? document.documentElement;
    target.appendChild(this.shellElement);
    this.logger.debug('Widget UI injected');
  }

  /**
   * Set up event handlers
   */
  private setupEventHandlers(): void {
    eventBus.on('settings:change', () => {
      const storageKey = `aura-${this.config?.appId}`;
      try {
        localStorage.setItem(storageKey, stateManager.toJson());
      } catch (_) {
        this.logger.warn('localStorage unavailable, settings not persisted');
      }
    });

    eventBus.on('settings:change', (settings) => {
      this.config?.onSettingsChange?.(settings as AuraSettings);
    });

    domScanner.onScan(() => {
      this.applyCurrentSettings();
    });
  }

  /**
   * Apply current settings to the page
   */
  private applyCurrentSettings(): void {
    // This will be implemented by feature modules
    eventBus.emit('settings:change', stateManager.getSettings());
  }

  /**
   * Get the public API
   */
  getApi(): AuraAPI {
    return {
      open: () => stateManager.setPanelOpen(true),
      close: () => stateManager.setPanelOpen(false),
      toggle: () => stateManager.setPanelOpen(!stateManager.getState().isOpen),
      hide: () => stateManager.setHidden(true),
      show: () => stateManager.setHidden(false),
      getSettings: () => stateManager.getSettings(),
      setProfile: (profileName: string) => {
        // Will be implemented by profiles module
        eventBus.emit('profile:activate', { profileName });
      },
      // Route through the profiles module so deactivation resets the
      // profile-defined keys to their defaults (matches UI toggle behaviour).
      clearProfile: () => profilesDeactivate(),
      reset: () => {
        stateManager.reset();
        const storageKey = `aura-${this.config?.appId}`;
        try {
          localStorage.removeItem(storageKey);
        } catch (_) {
          this.logger.warn('localStorage unavailable during reset');
        }
      },
      on: (event, callback) => eventBus.on(event as any, callback as any),
      off: (eventType) => eventBus.off(eventType as any),
      destroy: () => this.destroy(),
    };
  }

  /**
   * Destroy the widget and clean up
   */
  destroy(): void {
    this.logger.info('Destroying widget');

    domScanner.stop();
    destroyOrientationEffects();
    cssInjector.destroy();
    eventBus.clear();

    if (this.shellElement && this.shellElement.parentNode) {
      this.shellElement.parentNode.removeChild(this.shellElement);
    }

    this.initialized = false;
    this.config = null;
    this.shellElement = null;
  }
}

// Singleton instance
export const engine = new AuraEngine();
